import api from '../../utils/api';
import type {
  MediaAsset,
  MediaAssetListResponse,
  MediaAssetMeta,
  PublicMediaAsset,
  UpdateMediaAssetPayload,
  UploadMediaAssetPayload,
} from '../../models/MasterAdmin/mediaAsset';
import { normalizeMediaAsset } from '../../models/MasterAdmin/mediaAsset';

type ApiOne<T> = { success: boolean; data: T; message?: string };
type ApiList<T> = {
  success: boolean;
  data: T;
  message?: string;
  pagination?: MediaAssetListResponse['pagination'];
};

const BASE = '/master-admin/media';

export async function fetchMediaMeta(): Promise<MediaAssetMeta> {
  const res = await api.get<ApiOne<MediaAssetMeta>>(`${BASE}/meta`);
  return res.data.data;
}

export async function fetchMediaAssets(params?: {
  fileType?: string;
  key?: string;
  isActive?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<MediaAssetListResponse> {
  const res = await api.get<ApiList<MediaAsset[]>>(BASE, {
    params: {
      search: params?.search,
      key: params?.key,
      fileType: params?.fileType,
      page: params?.page,
      limit: params?.limit,
      isActive:
        params?.isActive === undefined ? undefined : params.isActive ? 'true' : 'false',
    },
  });
  return {
    data: (res.data.data || []).map((row) => normalizeMediaAsset(row)),
    pagination: res.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 1 },
  };
}

export async function fetchMediaAsset(id: number): Promise<MediaAsset> {
  const res = await api.get<ApiOne<MediaAsset>>(`${BASE}/${id}`);
  return normalizeMediaAsset(res.data.data);
}

export async function fetchMediaPreviewByKey(key: string): Promise<{
  fileUrl: string;
  fileType: string;
  mimeType: string;
  version: number;
}> {
  const res = await api.get<
    ApiOne<{ fileUrl: string; fileType: string; mimeType: string; version: number }>
  >(`${BASE}/preview/${encodeURIComponent(key)}`);
  return res.data.data;
}

export async function fetchPublicMediaByKey(key: string): Promise<PublicMediaAsset> {
  const res = await api.get<ApiOne<PublicMediaAsset>>(`/media/key/${encodeURIComponent(key)}`);
  return res.data.data;
}

export async function uploadMediaAsset(payload: UploadMediaAssetPayload): Promise<MediaAsset> {
  const fd = new FormData();
  fd.append('file', payload.file);
  fd.append('key', payload.key);
  if (payload.name) fd.append('name', payload.name);

  const res = await api.post<ApiOne<MediaAsset>>(BASE, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return normalizeMediaAsset(res.data.data);
}

export async function updateMediaAsset(
  id: number,
  payload: UpdateMediaAssetPayload
): Promise<MediaAsset> {
  const fd = new FormData();
  if (payload.name !== undefined) fd.append('name', payload.name);
  if (payload.isActive !== undefined) fd.append('isActive', String(payload.isActive));
  if (payload.file) fd.append('file', payload.file);

  const res = await api.patch<ApiOne<MediaAsset>>(`${BASE}/${id}`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return normalizeMediaAsset(res.data.data);
}

export async function deleteMediaAsset(id: number): Promise<void> {
  await api.delete(`${BASE}/${id}`);
}

export async function toggleMediaAssetActive(id: number, isActive: boolean): Promise<MediaAsset> {
  return updateMediaAsset(id, { isActive });
}
